import { useCallback, useEffect, useState } from "react";

import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";

import { LoadingPage } from "@/components/loading-page";
import { LoadingQuill } from "@/components/loading-quill";
import { useAppDispatch, useAppSelector } from "@/data/hooks";
import { selectHasAutoSubmission } from "@/features/assignment";
import { selectHasFinal } from "@/features/authority";
import { updateSocraticDialogueTimeline } from "@/features/socratic";
import { DialoguePanel } from "@/features/socratic/components/dialogue-panel";
import { PreparationPhaseTip } from "@/features/socratic/components/preparation-phase-tip";
import { RecordingStartCutoffTip } from "@/features/socratic/components/recording-start-cutoff-tip";
import { SocraticDialogueControls } from "@/features/socratic/components/socratic-dialogue-controls";
import { SocraticTimers } from "@/features/socratic/components/timers/socratic-timers";
import { hasDialogueResponse } from "@/features/socratic/helpers/has-dialogue-response";
import {
  usePreparationCountdown,
  usePreparationRecording,
  useSocraticRecording,
} from "@/features/socratic/hooks";
import { useCompleteSocraticTurn } from "@/features/socratic/hooks/use-complete-socratic-turn";
import { useRecordPreparationMedia } from "@/features/socratic/hooks/use-record-preparation-media";
import {
  selectPreparationEndDatePassed,
  selectReattemptWindowEndDate,
  selectReattemptWindowEndDatePassed,
  selectRecordingStartCutoffDatePassed,
} from "@/features/socratic/socratic-slice";
import { StartedDialogue } from "@/features/socratic/types";
import {
  AnswerBlockFragment,
  useStartSocraticTurnMutation,
} from "@/generated/graphql";

import { useQueueVideoResponse } from "../../hooks/use-queue-video-response";
import { ReattemptTimerTip } from "../reattempt-timer-tip";
import { SocraticVideoCapture } from "../video-capture";
import * as styles from "./socratic-dialogue.css";

interface Props {
  startedDialogue: StartedDialogue;
  workId: string;
  answerBlock: AnswerBlockFragment;
  onReattemptPressed: () => void;
  numAttemptsRemaining: number;
}

/**
 * SocraticDialogue is the main content of the socratic flow.
 *
 * It is responsible for a single attempt (Socratic Dialogue).
 */
export function SocraticDialogue(props: Props) {
  const {
    startedDialogue,
    workId,
    answerBlock,
    onReattemptPressed,
    numAttemptsRemaining,
  } = props;
  const {
    readingTimeSeconds: preparationTime,
    responseTimeSeconds: responseTime,
  } = startedDialogue;
  const [firstTurn] = startedDialogue.turns;

  const dispatch = useAppDispatch();

  const reattemptWindowEndDate = useAppSelector(selectReattemptWindowEndDate);
  const reattemptWindowEndDatePassed = useAppSelector(
    selectReattemptWindowEndDatePassed
  );
  const recordingStartCutoffDatePassed = useAppSelector(
    selectRecordingStartCutoffDatePassed
  );
  const hasAutoSubmission = useAppSelector(selectHasAutoSubmission);
  const isSubmitted = useAppSelector(selectHasFinal);
  const preparationEndDatePassed = useAppSelector(
    selectPreparationEndDatePassed
  );
  const preparationCountdown = usePreparationCountdown();

  const [startTurnMutation] = useStartSocraticTurnMutation();

  const snapshotVideoResponse = useQueueVideoResponse({
    answerBlock,
  });

  const [onCaptureReady, isUploading, retryUpload] = useCompleteSocraticTurn({
    workId,
    turnId: firstTurn?.id,
    dialogueId: startedDialogue.id,
    // NOTE: Currently, the dialogue has one turn, so we queue the video
    // response when the recording ends (onCapture), before upload starts,
    // so auto-submission sees the snapshot even if the upload is still in flight.
    onCapture: () => {
      snapshotVideoResponse({ dialogueId: startedDialogue.id });
    },
  });

  const onStartTurn = useCallback(() => {
    if (!firstTurn) return;
    startTurnMutation({
      variables: {
        workId,
        turnId: firstTurn.id,
      },
    });
  }, [firstTurn, startTurnMutation, workId]);

  // Preparation and response recorders share a video track — block the
  // response recorder's auto-start until preparation has released it.
  const [preparationStillRecording, setPreparationStillRecording] =
    useState(false);

  const recording = useSocraticRecording({
    onRecordingStart: onStartTurn,
    onCaptureReady,
    blockAutoStart: preparationStillRecording,
  });

  const { onUploaded, onUploadFailed } = useRecordPreparationMedia({
    workId,
    turnId: firstTurn?.id,
    dialogueId: startedDialogue.id,
  });

  usePreparationRecording({
    stream: recording.recorder.stream,
    preparationEndDatePassed: preparationEndDatePassed === true,
    maxDurationSeconds: preparationTime,
    turnId: firstTurn?.id,
    onUploaded,
    onUploadFailed,
    onIsRecordingChange: setPreparationStillRecording,
  });

  const isDialogueCompleted = hasDialogueResponse(startedDialogue);

  // Initialise Socratic slice with timing data
  useEffect(() => {
    dispatch(
      updateSocraticDialogueTimeline({
        dialogueId: startedDialogue.id,
        startedAt: startedDialogue.startedAt,
        preparationTime,
        responseTime,
      })
    );
  }, [
    startedDialogue.id,
    startedDialogue.startedAt,
    dispatch,
    preparationTime,
    responseTime,
  ]);

  // TODO: Handle case where there are no turns
  if (!firstTurn) return null;

  const wouldAutoSubmitDuringRecording =
    hasAutoSubmission && recordingStartCutoffDatePassed === true;

  const reattemptDisabled =
    reattemptWindowEndDatePassed === true ||
    isUploading ||
    wouldAutoSubmitDuringRecording;

  const showRecordingStartCutoffTip =
    reattemptWindowEndDate == null || reattemptWindowEndDatePassed === false;

  if (isSubmitted && isUploading) {
    const message =
      "Almost done — please keep this page open while your response finishes uploading.";
    return <LoadingPage message={message} centered />;
  }

  return (
    <PanelGroup direction="horizontal" className={styles.content}>
      <Panel
        id="dialogue-panel"
        order={1}
        defaultSize={34}
        minSize={30}
        maxSize={50}
      >
        <DialoguePanel
          answerBlock={answerBlock}
          turn={firstTurn}
          responseTimeSeconds={startedDialogue.responseTimeSeconds}
        />
      </Panel>

      <PanelResizeHandle className={styles.handle} />

      <Panel id="dialogue-main" order={2}>
        <div className={styles.main}>
          {isUploading ? (
            <div className={styles.webcamContainer({ uploading: true })}>
              <LoadingQuill message="Uploading your response..." />
            </div>
          ) : (
            <div className={styles.webcamContainer()}>
              <SocraticVideoCapture
                webcam={recording.recorder}
                preparationCountdown={preparationCountdown}
                dialogueId={startedDialogue.id}
              />
            </div>
          )}
          <div className={styles.controls}>
            <SocraticTimers
              webcam={recording.recorder}
              preparationCountdown={preparationCountdown}
            />

            <SocraticDialogueControls
              hasRecordingPermission={recording.recorder.hasPermission}
              onStartEarlyPressed={recording.startRecordingEarly}
              onStopEarlyPressed={recording.stopRecordingEarly}
              onRetakePressed={onReattemptPressed}
              isDialogueCompleted={isDialogueCompleted}
              reattemptDisabled={reattemptDisabled}
              numAttemptsRemaining={numAttemptsRemaining}
              retryUpload={retryUpload}
            />
          </div>
          <PreparationPhaseTip
            preparationEndDatePassed={preparationEndDatePassed === true}
            isRecording={recording.recorder.isRecording}
            isDialogueCompleted={isDialogueCompleted}
          />
          {isDialogueCompleted && numAttemptsRemaining > 0 && (
            <div className={styles.tips}>
              {reattemptWindowEndDate != null && (
                <ReattemptTimerTip
                  endDate={reattemptWindowEndDate}
                  endDatePassed={reattemptWindowEndDatePassed === true}
                />
              )}
              {showRecordingStartCutoffTip && (
                <RecordingStartCutoffTip
                  isFirstAttempt={false}
                  hasAutoSubmission={hasAutoSubmission}
                />
              )}
            </div>
          )}
        </div>
      </Panel>
    </PanelGroup>
  );
}
